/**
 * Reactive kiosk-config singleton.
 *
 * Fetches the `admin_frontend.kiosks` row for the active kiosk slug
 * exactly once per app lifetime and caches the response in module-scope
 * refs. Every downstream consumer (pricing, print routing, payment
 * routing) reads from these refs instead of re-fetching.
 *
 * Singleton pattern follows {@link useEntrAppController} — refs live
 * at module scope so two components calling `useKioskConfig()` see
 * the same state without prop drilling.
 */

import { type ComputedRef, computed, type Ref, ref, shallowRef } from 'vue';
import {
  type KioskPrinterConfig,
  resolveKioskPrinterConfig,
} from '@/kiosk-core';
import { configuredString } from '@/kiosk-core/config/kioskRuntimeConfig';
import { addBreadcrumb, captureException } from '@/telemetry/sentry';
import {
  fetchKioskConfig,
  fetchPartner,
  type KioskConfig,
  type Partner,
} from '@/transport/kioskConfigApi';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

// `shallowRef` so the cached config keeps reference identity — Vue's
// default `ref()` wraps objects in a reactive proxy, which would make
// `expect(cached).toBe(originalResponse)` fail even though the data
// is the same. We never mutate fields on the kiosk config; it's a
// snapshot loaded once per kiosk lifetime.
const kioskRef = shallowRef<KioskConfig | null>(null);
const partnerRef = shallowRef<Partner | null>(null);
const stateRef = ref<LoadState>('idle');
const errorRef = shallowRef<Error | null>(null);

/**
 * Tracks the in-flight load so concurrent callers share a single
 * fetch instead of stampeding the backend.
 */
let inflight: Promise<KioskConfig> | null = null;
let inflightSlug: string | null = null;

function resolveDefaultSlug(): string | null {
  // Build-time bake — never read from the URL here; callers pass the
  // runtime slug from urlState explicitly.
  return import.meta.env.VITE_KIOSK_SLUG?.trim() || null;
}

/**
 * Load the kiosk config for `slug`. Subsequent calls with the same
 * slug return the cached value. A call with a different slug refetches
 * and replaces the cache — useful for multi-tenant dev only; prod
 * kiosks bake one slug into the bundle.
 *
 * Errors are captured, stored on `errorRef` and re-thrown so the caller
 * can route to a fatal sheet.
 */
export async function loadKioskConfig(
  slug: string | null = resolveDefaultSlug()
): Promise<KioskConfig> {
  if (!slug) {
    const err = new Error('kiosk_config_missing_slug');
    errorRef.value = err;
    stateRef.value = 'error';
    throw err;
  }

  if (kioskRef.value?.slug === slug && stateRef.value === 'ready') {
    return kioskRef.value;
  }

  if (inflight && inflightSlug === slug) return inflight;
  if (inflight) {
    await inflight.catch(() => null);
  }

  stateRef.value = 'loading';
  errorRef.value = null;
  inflightSlug = slug;
  inflight = fetchKioskConfig(slug)
    .then(async (response) => {
      const partnerId = configuredString(response.partner);
      const paymentDestination = configuredString(response.payment_destination);
      const partner =
        paymentDestination === 'partner' && partnerId
          ? await loadPartnerConfig(partnerId)
          : null;
      kioskRef.value = response;
      partnerRef.value = partner;
      stateRef.value = 'ready';

      addBreadcrumb('kiosk.config', 'Kiosk config loaded', {
        slug,
        // Full kiosk UUID is intentional Sentry metadata for config row joins.
        kiosk_uuid: response.uuid,
        bucket: response.portrait_s3_bucket_name,
        style_count: response.portrait_style_ids?.length ?? 0,
        currency: response.currency,
        price: response.price,
      });
      return response;
    })
    .catch((error: unknown) => {
      const err = error instanceof Error ? error : new Error(String(error));
      errorRef.value = err;
      stateRef.value = 'error';
      captureException(err, 'kiosk.config.load');
      throw err;
    })
    .finally(() => {
      inflight = null;
      inflightSlug = null;
    });

  return inflight;
}

/**
 * Test-only — drops the cache and clears wired state. Production
 * code never calls this; one config per kiosk lifetime.
 */
export function resetKioskConfig(): void {
  kioskRef.value = null;
  partnerRef.value = null;
  stateRef.value = 'idle';
  errorRef.value = null;
  inflight = null;
  inflightSlug = null;
}

export interface UseKioskConfig {
  state: Readonly<Ref<LoadState>>;
  error: Readonly<Ref<Error | null>>;
  isReady: ComputedRef<boolean>;

  /** Curated style IDs to expand on the result grid. */
  styleIds: ComputedRef<string[]>;
  /** CUPS printer name baked into the kiosk row. */
  printerName: ComputedRef<string | null>;
  /** Orientation-aware print config resolved from kiosk printer_details. */
  printerConfig: ComputedRef<KioskPrinterConfig | null>;
  /** ISO currency code for UI display; payment adapters lowercase for Stripe. */
  currency: ComputedRef<string | null>;
  /** Per-portrait price in major units; coerced to number. */
  pricePerPhoto: ComputedRef<number | null>;
  /** Per-group photo price in major units; coerced to number. */
  groupPhotoPrice: ComputedRef<number | null>;
  /** Demographics confirmations required before generation. */
  demographicsRequired: ComputedRef<string[]>;
  /** Max parallel generation calls (kiosk-tuned). */
  parallelGenerations: ComputedRef<number>;
  /** Whether the style picker UI should render. */
  styleSelectorEnabled: ComputedRef<boolean>;
  /** Backend-configured Stripe Terminal gate. */
  terminalPaymentEnabled: ComputedRef<boolean | null>;
  /** Backend-configured payment destination. */
  paymentDestination: ComputedRef<string | null>;
  /** Connected Stripe account used when paymentDestination is partner. */
  stripeAccountId: ComputedRef<string | null>;

  load: typeof loadKioskConfig;
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function clampParallel(value: unknown): number {
  const n = coerceNumber(value);
  if (n === null || n <= 0) return 1;
  return Math.min(Math.max(1, Math.floor(n)), 8);
}

export function useKioskConfig(): UseKioskConfig {
  return {
    // Module-scope + `shallowRef` already give us the semantic "snapshot,
    // don't mutate" posture. The runtime `readonly()` wrapper from Vue
    // produces a `DeepReadonly<Ref<KioskConfig>>` whose nested array
    // fields (e.g. `portrait_style_ids: readonly string[]`) no longer
    // assignable-from a `KioskConfig` that types them mutable. Casting
    // at the boundary keeps the public contract honest without
    // rewriting every wire type as `readonly`.
    state: stateRef as Readonly<Ref<LoadState>>,
    error: errorRef as Readonly<Ref<Error | null>>,
    isReady: computed(() => stateRef.value === 'ready'),

    styleIds: computed(() => {
      const ids = kioskRef.value?.portrait_style_ids;
      return Array.isArray(ids) ? ids.filter((s) => typeof s === 'string') : [];
    }),
    printerName: computed(() => configuredString(kioskRef.value?.printer)),
    printerConfig: computed(() => {
      const kiosk = kioskRef.value;
      if (!kiosk) return null;
      const fallbackPrinter = configuredString(kiosk.printer);
      const verticalPrinter = configuredString(kiosk.printer_details?.id);
      const horizontalPrinter = configuredString(
        kiosk.printer_details?.id_horizontal
      );
      if (!verticalPrinter && !horizontalPrinter) {
        return resolveKioskPrinterConfig({ printerId: fallbackPrinter });
      }
      return resolveKioskPrinterConfig({
        verticalPrinterId: verticalPrinter,
        horizontalPrinterId: horizontalPrinter,
      });
    }),
    currency: computed(() => {
      const c = kioskRef.value?.currency;
      return typeof c === 'string' && c.trim() ? c.trim().toUpperCase() : null;
    }),
    pricePerPhoto: computed(() => coerceNumber(kioskRef.value?.price)),
    groupPhotoPrice: computed(() => coerceNumber(kioskRef.value?.price_multi)),
    demographicsRequired: computed(() => {
      const d = kioskRef.value?.demographics_confirmations;
      return Array.isArray(d) ? d.filter((s) => typeof s === 'string') : [];
    }),
    parallelGenerations: computed(() =>
      clampParallel(kioskRef.value?.parallel_generations)
    ),
    styleSelectorEnabled: computed(
      () => kioskRef.value?.portrait_styles_enable_selector === true
    ),
    terminalPaymentEnabled: computed(() => {
      const value = kioskRef.value?.is_stripe_terminal_payment;
      return typeof value === 'boolean' ? value : null;
    }),
    paymentDestination: computed(
      () => configuredString(kioskRef.value?.payment_destination) ?? 'platform'
    ),
    stripeAccountId: computed(() =>
      configuredString(partnerRef.value?.stripe_account_id)
    ),

    load: loadKioskConfig,
  };
}

async function loadPartnerConfig(partnerId: string): Promise<Partner | null> {
  try {
    return await fetchPartner(partnerId);
  } catch (error) {
    captureException(error, 'kiosk.config.partner.load');
    addBreadcrumb('kiosk.config', 'Partner config load failed', {
      partner_id: partnerId,
    });
    return null;
  }
}

/** Test-only export. Never imported from production code. */
export const useKioskConfigInternals = {
  /** Inspect the inflight promise (for testing dedup behaviour). */
  hasInflight: () => inflight !== null,
  inflightSlug: () => inflightSlug,
};
